import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, VStack, ScrollView, Spinner, Center,
} from '@gluestack-ui/themed';
import { useAuthStore } from '@/common/auth/store';
import { useDialog } from '@/common/context/DialogContext';
import { composeEmail } from '@/common/utils/composeEmail';
import JourneyMatrix from './components/JourneyMatrix';
import EngagementHeader from './components/EngagementHeader';
import StudentWorkDrawer from './components/StudentWorkDrawer';
import { useEngagementData } from './hooks/useEngagementData';
import { useEngagementStore } from './EngagementStore';

/**
 * Engagement - The next-generation Evaluation & Interaction Cockpit.
 * Focused on "Humans, not Rows".
 */
const Engagement: React.FC = () => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const lecturerEmail = useAuthStore((state) => state.user?.email);

  const {
    groupedByStudent,
    stalledCount,
    availableModules,
    availableVersions,
    isLoading,
  } = useEngagementData();

  const { filter, setFilter } = useEngagementStore();

  const totalStudents = groupedByStudent.length;

  const emailRecipients = useMemo(() => {
    const emails = new Set<string>();
    groupedByStudent.forEach((group) => {
      const email = group.student.email?.trim();
      if (email) {
        emails.add(email);
      }
    });
    return Array.from(emails);
  }, [groupedByStudent]);

  const selectedModuleTitle = availableModules.find(
    (module) => module.id === filter.moduleId,
  )?.title;

  const emailAllDisabledHint = useMemo(() => {
    if (!filter.moduleId) {
      return t('lecturer.engagement.email.disabledNoModule');
    }
    if (!lecturerEmail) {
      return t('lecturer.engagement.email.disabledNoLecturerEmail');
    }
    if (emailRecipients.length === 0) {
      return t('lecturer.engagement.email.disabledNoRecipients');
    }
    return undefined;
  }, [filter.moduleId, lecturerEmail, emailRecipients.length, t]);

  const isEmailAllDisabled = !!emailAllDisabledHint;

  const handleEmailAll = useCallback(async () => {
    if (isEmailAllDisabled || !lecturerEmail || !selectedModuleTitle) {
      return;
    }

    const subject = filter.moduleVersion
      ? t('lecturer.engagement.email.subjectWithVersion', {
        moduleTitle: selectedModuleTitle,
        moduleVersion: filter.moduleVersion,
      })
      : t('lecturer.engagement.email.subject', { moduleTitle: selectedModuleTitle });

    try {
      await composeEmail({
        to: [lecturerEmail],
        bcc: emailRecipients,
        subject,
        body: t('lecturer.engagement.email.greeting'),
      });
    } catch {
      showToastMessage('error', t('lecturer.engagement.email.openError'));
    }
  }, [
    emailRecipients,
    filter.moduleVersion,
    isEmailAllDisabled,
    lecturerEmail,
    selectedModuleTitle,
    showToastMessage,
    t,
  ]);

  if (isLoading) {
    return (
      <Center flex={1}>
        <Spinner size="large" />
      </Center>
    );
  }

  return (
    <Box flex={1} bg="$bgApp">
      <EngagementHeader
        totalStudents={totalStudents}
        stalledCount={stalledCount}
        availableModules={availableModules}
        availableVersions={availableVersions}
        selectedModuleId={filter.moduleId}
        selectedModuleVersion={filter.moduleVersion}
        selectedWorkState={filter.workState ?? 'all'}
        onSelectModule={(id) => setFilter({
          moduleId: id || undefined,
          moduleVersion: undefined,
        })}
        onSelectModuleVersion={(version) => setFilter({ moduleVersion: version || undefined })}
        onSelectWorkState={(workState) => setFilter({ workState })}
        onEmailAll={handleEmailAll}
        isEmailAllDisabled={isEmailAllDisabled}
        emailAllDisabledHint={emailAllDisabledHint}
      />

      <ScrollView flex={1}>
        <VStack space="2xl" pt="$2" pb="$6" px="$8" maxWidth={1400} alignSelf="center" w="100%">
          {/* Zone C: Coaching Hub (Monitoring Zone - Human-Centric) */}
          <JourneyMatrix groups={groupedByStudent} />
        </VStack>
      </ScrollView>

      {/* The Lens: Inspection Drawer */}
      <StudentWorkDrawer />
    </Box>
  );
};

export default Engagement;
